import { getRuntimeConfig } from '../config/runtime';
import { readCurrentWarehouseId } from '../../features/warehouses/currentWarehouse';
import { CORRELATION_HEADER, newCorrelationId } from './correlation';
import { ApiError, type ProblemDetails } from './problem';

/**
 * Thin fetch wrapper. Every request:
 *  - Gets a fresh ULID `X-Correlation-Id` (or reuses one passed in via init)
 *  - Sends JSON by default
 *  - Throws `ApiError` (with the parsed Problem Details payload) on non-2xx
 *  - Echoes the correlation ID on the resolved/rejected value so callers
 *    can surface it in toasts and error pages
 *
 * Real OpenAPI-typed wrappers (via `openapi-fetch`) land per feature module
 * starting in M1.6 (vertical slice) and M3+ (item / order / pick endpoints).
 * This module gives every later wrapper a single place to do correlation
 * and error contract handling.
 */

/**
 * Resolve the API base URL at call time:
 *   - In dev (Vite proxy active), `import.meta.env.VITE_API_BASE_URL` is
 *     optional; the dev proxy maps "/api" → http://localhost:5080.
 *   - In a deployed (built) app, the value comes from /config.json, loaded
 *     by `loadRuntimeConfig()` before the first request.
 * Cookies need credentials: 'include' on every cross-origin call so the
 * auth cookie set by /v1/registration/.../complete is sent back on
 * subsequent requests.
 */
function resolveBaseUrl(): string {
  const envOverride = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (envOverride) return envOverride.replace(/\/+$/, '');
  if (import.meta.env.DEV) return '/api';
  // Production / preview: runtime config must have been loaded by main.tsx.
  return `${getRuntimeConfig().apiBaseUrl}/api`;
}

/** Names the warehouse a request is about. Matches HttpCurrentWarehouse.HeaderName on the API. */
export const WAREHOUSE_HEADER = 'X-Warehouse-Id';

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  correlationId?: string;
}

/**
 * Global "session expired" hook. Registered once at startup (main.tsx) to
 * clear the cached user and redirect to /login when a *business* request comes
 * back 401. The auth endpoints themselves (/v1/auth/*) are exempt — they use
 * 401 as a normal answer (anonymous /me, bad credentials, MFA challenge) and
 * handle it in-place, so bouncing on those would cause a redirect loop.
 */
type UnauthorizedHandler = (path: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export interface ApiResponse<T> {
  data: T;
  correlationId: string;
  status: number;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const { body, correlationId, headers, ...rest } = options;
  const id = correlationId ?? newCorrelationId();

  // The warehouse this session is working in. Permissions resolve per warehouse, so a
  // warehouse-scoped endpoint answers 403 without it rather than guessing — see
  // features/warehouses/currentWarehouse for why it is read from a store rather than passed in.
  //
  // Omitted rather than sent empty when nothing is chosen yet: an empty header is a malformed one,
  // and the endpoints that do not need a warehouse (sign-in, the warehouse list itself) must keep
  // working before one exists.
  const currentWarehouseId = readCurrentWarehouseId();

  const init: RequestInit = {
    ...rest,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      [CORRELATION_HEADER]: id,
      ...(currentWarehouseId ? { [WAREHOUSE_HEADER]: currentWarehouseId } : {}),
      // Last, so a caller can override it — which only the warehouse switcher needs to, and only
      // to read a warehouse it is about to move to.
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const url = path.startsWith('http') ? path : `${resolveBaseUrl()}${path}`;
  const response = await fetch(url, init);
  const serverCorrelationId = response.headers.get(CORRELATION_HEADER) ?? id;

  if (!response.ok) {
    let problem: ProblemDetails;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = {
        title: response.statusText || 'Request failed',
        status: response.status,
        correlationId: serverCorrelationId,
      };
    }
    // The server may not echo correlationId in the body in dev/test paths.
    problem.correlationId ??= serverCorrelationId;
    // A 401 on a business call means the session lapsed — hand off to the
    // global handler (redirect to /login). The auth endpoints are exempt: they
    // own their 401s (anonymous /me, bad credentials, MFA challenge).
    if (response.status === 401 && !path.startsWith('/v1/auth/')) {
      unauthorizedHandler?.(path);
    }
    throw new ApiError(response.status, problem);
  }

  // 204 No Content and HEAD requests return no body.
  const data =
    response.status === 204 || init.method === 'HEAD'
      ? (undefined as unknown as T)
      : ((await response.json()) as T);

  return { data, correlationId: serverCorrelationId, status: response.status };
}


/**
 * Fetch binary content and hand back an object URL.
 *
 * WHY THIS EXISTS RATHER THAN AN <img src> POINTING AT THE API
 * -----------------------------------------------------------
 * An <img> tag issues a plain GET. It would carry the session cookie — the cookie is
 * SameSite=None because the PWA and the API are separate origins — but it CANNOT carry
 * X-Warehouse-Id, and every warehouse-scoped endpoint refuses a request without it. So an item
 * photo simply would not load.
 *
 * The alternative was exempting the image endpoint from warehouse scoping so a bare <img> could
 * reach it. That was rejected: for the 3PL tenant each warehouse is a different end client, and
 * a photo of client A's stock is client A's. Fetching it properly and wrapping the bytes in an
 * object URL keeps the boundary intact and costs one extra hop.
 *
 * The caller OWNS the returned URL and must revokeObjectURL it when the element goes away, or the
 * blob stays in memory for the life of the document.
 */
export async function apiObjectUrl(path: string, signal?: AbortSignal): Promise<string> {
  const currentWarehouseId = readCurrentWarehouseId();
  const id = newCorrelationId();

  const response = await fetch(path.startsWith('http') ? path : `${resolveBaseUrl()}${path}`, {
    credentials: 'include',
    signal,
    headers: {
      [CORRELATION_HEADER]: id,
      ...(currentWarehouseId ? { [WAREHOUSE_HEADER]: currentWarehouseId } : {}),
    },
  });

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.(path);
    throw new ApiError(response.status, {
      title: response.statusText || 'Could not load the image',
      status: response.status,
      correlationId: response.headers.get(CORRELATION_HEADER) ?? id,
    });
  }

  return URL.createObjectURL(await response.blob());
}

/**
 * POST a file as multipart/form-data.
 *
 * Content-Type is deliberately NOT set: the browser has to add it itself so it can append the
 * multipart boundary, and setting it by hand produces a body the server cannot parse.
 *
 * X-Warehouse-Id is not only scoping here. multipart/form-data is a CORS "simple request", so a
 * cross-site <form> could otherwise post to the API with the session cookie attached; a custom
 * header cannot be set by a plain form, so sending one forces the preflight that CORS refuses.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  fieldName = 'file',
  fields?: Record<string, string>,
): Promise<ApiResponse<T>> {
  const currentWarehouseId = readCurrentWarehouseId();
  const id = newCorrelationId();

  const form = new FormData();
  form.append(fieldName, file, file.name);
  // Extra text parts travel in the same request as the file. A document's description arrives
  // this way rather than as a follow-up PATCH, so a failure between the two cannot leave a file
  // attached with the words the operator typed thrown away.
  for (const [key, value] of Object.entries(fields ?? {})) form.append(key, value);

  const response = await fetch(path.startsWith('http') ? path : `${resolveBaseUrl()}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
    headers: {
      Accept: 'application/json',
      [CORRELATION_HEADER]: id,
      ...(currentWarehouseId ? { [WAREHOUSE_HEADER]: currentWarehouseId } : {}),
    },
  });

  const serverCorrelationId = response.headers.get(CORRELATION_HEADER) ?? id;

  if (!response.ok) {
    let problem: ProblemDetails;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = {
        title: response.statusText || 'Upload failed',
        status: response.status,
        correlationId: serverCorrelationId,
      };
    }
    problem.correlationId ??= serverCorrelationId;
    if (response.status === 401) unauthorizedHandler?.(path);
    throw new ApiError(response.status, problem);
  }

  return {
    data: (await response.json()) as T,
    correlationId: serverCorrelationId,
    status: response.status,
  };
}
